import React from 'react';
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Skeleton,
} from '@mui/material';
import type { ISODate, SimpleSchedule, SimpleDaySchedule } from '../../common/schedulePickerUtil';

type PickerMode = 'ASAP' | 'SCHEDULED';

export interface DeliveryTimeChange {
  mode: PickerMode;
  date?: ISODate;
  slotIndex?: number;
}

export interface DeliveryTimePickerProps {
  schedule: SimpleSchedule;
  mode?: PickerMode;
  selectedDate?: ISODate;
  selectedSlotIndex?: number;
  loading?: boolean;
  disabled?: boolean;
  onChange?: (value: DeliveryTimeChange) => void;
}

const jaWeekdays = ['日', '月', '火', '水', '木', '金', '土'];
function formatDateLabel(date: ISODate) {
  return `${date.month() + 1}/${date.date()} ${jaWeekdays[date.day()]}`;
}

function findDay(schedule: SimpleSchedule, date?: ISODate): SimpleDaySchedule | undefined {
  if (!date) return undefined;
  return schedule.find((d) => d.date.isSame(date, 'day'));
}

export const DeliveryTimePicker: React.FC<DeliveryTimePickerProps> = ({
  schedule,
  mode: modeProp,
  selectedDate: selectedDateProp,
  selectedSlotIndex: selectedSlotIndexProp,
  loading,
  disabled,
  onChange,
}) => {
  const firstDate = schedule[0]?.date;

  const [mode, setMode] = React.useState<PickerMode>(modeProp ?? 'ASAP');
  const [date, setDate] = React.useState<ISODate | undefined>(selectedDateProp ?? firstDate);
  const [slotIndex, setSlotIndex] = React.useState<number | undefined>(selectedSlotIndexProp);

  React.useEffect(() => {
    if (modeProp) setMode(modeProp);
  }, [modeProp]);
  React.useEffect(() => {
    if (selectedDateProp !== undefined) setDate(selectedDateProp);
  }, [selectedDateProp]);
  React.useEffect(() => {
    if (selectedSlotIndexProp !== undefined) setSlotIndex(selectedSlotIndexProp);
  }, [selectedSlotIndexProp]);

  const emit = React.useCallback(
    (next: Partial<DeliveryTimeChange>) => {
      const value: DeliveryTimeChange = {
        mode,
        date,
        slotIndex,
        ...next,
      };
      onChange?.(value);
    },
    [mode, date, slotIndex, onChange]
  );

  const handleMode = (_: unknown, next: PickerMode | null) => {
    if (!next) return;
    setMode(next);
    emit({ mode: next });
  };

  const handleSelectDate = (d: ISODate) => {
    setDate(d);
    setSlotIndex(undefined);
    emit({ date: d, slotIndex: undefined });
  };

  const handleSelectSlot = (idx: number) => {
    setSlotIndex(idx);
    emit({ slotIndex: idx });
  };

  const day = findDay(schedule, date);
  const noAvailableOnDay = day && day.slots.every((s) => !s.enabled);

  return (
    <Stack
      spacing={2}
      alignItems='center'
      sx={{
        width: '100%',
        marginX: 'auto',
        paddingX: { xs: 1.5, sm: 2 },
        backgroundColor: 'background.paper',
        padding: { xs: 1.5, sm: 2 },
        borderRadius: { xs: 0, sm: 2 },
        justifyContent: 'center'
      }}
    >
      {/* モード切替 */}
      <ToggleButtonGroup
        exclusive
        value={mode}
        onChange={handleMode}
        size="small"
        sx={{
          alignSelf: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          padding: 0.5,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: 'center',
          flexWrap: 'nowrap', // ボタン自体が折り返されないように
        }}
        disabled={disabled}
      >
        <ToggleButton value="ASAP" sx={{ flex: 1, whiteSpace: 'nowrap', minWidth: 120 }}>最速で届ける</ToggleButton>
        <ToggleButton value="SCHEDULED" sx={{ flex: 1, whiteSpace: 'nowrap', minWidth: 140 }}>日時を指定する</ToggleButton>
      </ToggleButtonGroup>

      {/* モード別表示 */}
      {loading ? (
        <Skeleton variant="rounded" height={64} sx={{ width: '100%' }} />
      ) : mode === 'ASAP' ? (
        <Box
          sx={{
            backgroundColor: 'primary',
            color: 'primary.dark',
            borderRadius: 2,
            padding: { xs: 1.5, sm: 2 },
            width: '100%',
            opacity: disabled ? 0.5 : 1,
            textAlign: 'center'
          }}
        >
            <Typography variant="subtitle1" fontWeight={700}>
              本日 13:20 頃到着予定
            </Typography>
            <Typography variant="body2" color="text.secondary">
              最も早い便で配送します。
            </Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ width: '100%' }}>
          {/* 日付選択（横スクロール） */}
          <Box sx={{ overflowX: 'auto', paddingBottom: 0.5 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: { xs: 'flex-start', sm: 'center' }, minWidth: 'fit-content' }}
            >
              {schedule.map((d) => {
                const allDisabled = d.slots.every((s) => !s.enabled);
                const selectedDate = date ? d.date.isSame(date, 'day') : false;
                return (
                  <Button
                    key={d.date.format('YYYY-MM-DD')}
                    variant={selectedDate ? 'contained' : 'outlined'}
                    color={selectedDate ? 'primary' : 'inherit'}
                    onClick={() => handleSelectDate(d.date)}
                    disabled={disabled || allDisabled}
                    sx={{
                      whiteSpace: 'nowrap',
                      borderRadius: 2,
                      paddingX: { xs: 1.5, sm: 2 },
                      flexShrink: 0,
                    }}
                  >
                    {formatDateLabel(d.date)}
                  </Button>
                );
              })}
            </Stack>
          </Box>

          {/* 時間帯選択 */}
          {day ? (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              useFlexGap
              sx={{
                width: '100%',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                flexWrap: 'wrap'
              }}
            >
              {day.slots.map((s, idx) => {
                const selected = slotIndex === idx;
                return (
                  <Button
                    key={`${day.date.format('YYYY-MM-DD')}-${idx}`}
                    variant={selected ? 'contained' : 'outlined'}
                    color={selected ? 'primary' : 'inherit'}
                    disabled={disabled || !s.enabled}
                    onClick={() => handleSelectSlot(idx)}
                    sx={{
                      minWidth: { xs: '100%', sm: 140 },
                      flex: { xs: '1 1 100%', sm: '0 0 auto' },
                      borderRadius: 2,
                    }}
                  >
                    {s.start.format('HH:mm')}-{s.end.format('HH:mm')}
                  </Button>
                );
              })}
              {noAvailableOnDay && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  選択可能な時間帯がありません。
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              日付を選択してください。
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default DeliveryTimePicker;